import React, { useState, useCallback, useContext } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { GiBanknote } from 'react-icons/gi';
import { GrConfigure } from 'react-icons/gr';
import './BankingNode.css'; // Import the updated CSS file
import BankingConfigModal from './BankingConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { notifyError } from '../../../errors/notifyError';
import { reconfigureNode, updateNodePosition as persistNodePosition } from '../../../api/api';

const BankingNode = ({ id, data, selected }) => {
  const { bankingType, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addBankingNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping, getNodeMapping } = useContext(NodeMappingContext);
  const upstreamUnitCount = (() => {
    const matcherEdge = edges.find((edge) => edge.target === id);
    const matcherNode = matcherEdge ? nodes.find((node) => node.id === matcherEdge.source) : null;
    if (!matcherNode) return 0;
    const consEdge = edges.find((edge) => edge.target === matcherNode.id && edge.targetHandle === 'cons_input');
    const sourceNode = consEdge ? nodes.find((node) => node.id === consEdge.source) : null;
    const sourceData = sourceNode?.data || {};
    const mode = sourceData.inputMode || sourceData.consumption_input_mode;
    if (mode !== 'multi_unit_slot_monthly') return 0;
    return sourceData.preview?.unitCount || sourceData.units?.length || 0;
  })();

  // Check if node is configured properly
  const isConfigured = data.isConfigured || data.isApiConfigured || !!bankingType;

  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);

  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    const sourceNodeId = inputEdge.source;
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    
    if (sourceNode?.data?.name) {
      return sourceNode.data.name;
    }
    
    const nodeMapping = getNodeMapping();
    return nodeMapping[sourceNodeId] || sourceNodeId;
  }, [edges, id, nodes, getNodeMapping]);

  const inputNodeName = getInputNodeName();

  // Show reconfigure when configured node's upstream changed or backend marks it stale
  const needsReconfiguration =
    data.needsReconfiguration ||
    data.isApiConfigured === false ||
    (isConfigured && data.inputNodeName && inputNodeName && data.inputNodeName !== inputNodeName) ||
    (isConfigured && data.inputNodeName && !hasInputConnection);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    const currentInputNodeName = getInputNodeName();
    if (!currentInputNodeName) {
      notifyError(new Error("Please connect an input to this banking node before reconfiguring it."), {
        toastId: 'banking.connectInputReconfigure',
      });
      return;
    }
    
    try {
      setIsConfiguring(true);
      await reconfigureNode(
        session.pipeline_name,
        name,
        currentInputNodeName
      );

      // Persist current node coordinates so any pipeline re-hydration keeps this location.
      if (session.pipeline_name && session.pipeline_name !== "Unsaved_Pipeline") {
        const position = getNodePosition();
        await persistNodePosition(
          session.pipeline_name,
          name,
          position.x,
          position.y
        );
      }

      reactFlowInstance.setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== id) return node;
          return {
            ...node,
            data: {
              ...node.data,
              inputNodeName: currentInputNodeName,
              needsReconfiguration: false,
              isConfigured: true,
              isApiConfigured: true
            }
          };
        })
      );
    } catch (error) {
      console.error('Error reconfiguring banking node input:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
    }
  };

  const openConfigModal = useCallback(() => {
    if (!hasInputConnection) {
      // Show an alert if there's no input connection
      notifyError(new Error("Please connect an input to this banking node before configuring it."), {
        toastId: 'banking.connectInput',
      });
      return;
    }
    
    // Open the modal for both new configuration and editing
    setIsModalOpen(true);
  }, [hasInputConnection]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, bankingType } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Add the banking node to the session state
      if (inputNodeName) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        // Now add the node to the session with the correct name
        addBankingNode(
          name,
          bankingType,
          inputNodeName,
          { x: position.x, y: position.y }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  bankingType,
                  inputNodeName,
                  needsReconfiguration: false,
                  isConfigured: true,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  bankingType,
                  inputNodeName: inputNodeName || null,
                  needsReconfiguration: false,
                  isConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }

      // Persist position after configure/edit so backend and canvas stay in sync.
      if (session.pipeline_name && session.pipeline_name !== "Unsaved_Pipeline") {
        await persistNodePosition(
          session.pipeline_name,
          name,
          position.x,
          position.y
        );
      }
    } catch (error) {
      console.error('Error configuring banking node:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getInputNodeName, getNodePosition, closeConfigModal, updateNodeMapping, addBankingNode, session, updateSelectedNode]);

  return (
    <>
      <div
        className={`banking-node ${selected ? 'selected' : needsReconfiguration ? 'needs-reconfiguration' : isConfigured ? 'configured' : 'not-configured'}`}
      >
        <div className={`banking-node-header ${needsReconfiguration ? 'needs-reconfiguration' : isConfigured ? 'configured' : 'not-configured'}`}>
          <div className="banking-node-header-title">
            <div className="banking-node-type">Banking</div>
            {name && isConfigured && <div className="banking-node-name-display">{name}</div>}
          </div>
          <div className="banking-node-header-icons">
            {needsReconfiguration ? (
              <button 
                onClick={handleReconfigureInput}
                className="banking-config-button banking-reconfigure-button"
                title="Reconfigure input node"
                disabled={!hasInputConnection || isConfiguring}
              >
                <GrConfigure className="banking-icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="banking-config-button"
                title={isConfigured ? "Edit Configuration" : hasInputConnection ? "Configure Banking" : "Connect an input first"}
                disabled={!hasInputConnection || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="banking-icon" />
                ) : (
                  <ExclamationTriangleIcon className="banking-icon" />
                )}
              </button>
            )}
            <GiBanknote className="banking-icon" />
          </div>
        </div>
        <div className="banking-node-content">
          <div className="banking-text-xs">
            {!hasInputConnection && (
              <div className="banking-text-warning">
                Connect an input to configure
              </div>
            )}
            {hasInputConnection && !bankingType && (
              <div className="banking-text-warning">
                Banking type not selected
              </div>
            )}
            {needsReconfiguration && (
              <div className="banking-text-warning">
                Input changed. Reconfigure required
              </div>
            )}
            {isConfiguring && (
              <div className="banking-text-info">
                Configuring...
              </div>
            )}
            Banking Type: {bankingType || 'not set'}
            {!!upstreamUnitCount && <div>Units: {upstreamUnitCount}</div>}
          </div>
          <div className="banking-flex banking-justify-between banking-items-center">
            <Handle
              type="target"
              position={Position.Left}
              id="input"
              className={`banking-connection-point ${isConfigured ? 'configured' : 'not-configured'}`}
              data-maxconnections="1"
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`banking-connection-point ${isConfigured ? 'configured-output' : 'not-configured'}`}
              isConnectable={isConfigured || needsReconfiguration}
              data-maxconnections="1"
            />
          </div>
        </div>
      </div>

      <BankingConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{ 
          name, 
          bankingType,
          inputNodeName: getInputNodeName(),
          position: getNodePosition()
        }}
      />
    </>
  );
};

export default BankingNode;
